exports.initializePersonaPage = function() {
    // --- ELEMENT SELECTORS ---
    const startButton = document.getElementById('start-button');
    if (!startButton) return; // Exit if not on the right page

    const mainContent = document.getElementById('main-content');
    const facetsContainer = document.getElementById('facets-container');
    const generatePromptButton = document.getElementById('generate-prompt-button');
    const resultsContainer = document.getElementById('results');
    const systemPromptTextarea = document.getElementById('system-prompt');
    const savePromptButton = document.getElementById('save-prompt-button');
    const savedPromptsList = document.getElementById('prompt-list');
    const savePromptModalEl = document.getElementById('savePromptModal');
    const savePromptModal = new bootstrap.Modal(savePromptModalEl);
    const savePromptForm = document.getElementById('save-prompt-form');
    const promptNameInput = document.getElementById('prompt-name');
    const promptDescriptionInput = document.getElementById('prompt-description');
    const startBlankButton = document.getElementById('start-blank-button');
    const cancelEditButton = document.getElementById('cancel-edit-button');

    // Scaffold, facet mapping and placeholder check come from the server
    // (persona_rules.page_data), so Python stays their one source (ADR-0076).
    const pageData = JSON.parse(document.getElementById('persona-page-data').textContent);
    const placeholderPattern = new RegExp(pageData.placeholder);
    const genericSaveError = 'Fehler beim Speichern der Persona.';

    // --- STATE ---
    let loadedFacets = [];
    let editingPromptId = null;  // set by startEdit, cleared by startNew and resetEditor
    let textChanged = false;     // set by the textarea's input event

    // --- CORE FUNCTIONS ---

    async function loadFacetsAndRender() {
        try {
            const response = await axios.get('/api/facets');
            loadedFacets = response.data;
            const groupedFacets = loadedFacets.reduce((acc, facet) => {
                const dim = facet.dimension;
                const subDim = facet.sub_dimension;
                if (!acc[dim]) acc[dim] = {};
                if (!acc[dim][subDim]) acc[dim][subDim] = [];
                acc[dim][subDim].push(facet);
                return acc;
            }, {});
            renderFacetAccordion(groupedFacets);
        } catch (error) {
            console.error('Error loading facets:', error);
            facetsContainer.innerHTML = '<div class="alert alert-danger">Fehler beim Laden der Persönlichkeits-Facetten.</div>';
        }
    }

    function renderFacetAccordion(groupedFacets) {
        let accordionId = 'facet-accordion';
        let html = `<div class="accordion" id="${accordionId}">`;
        let dimensionIndex = 0;
        for (const dimension in groupedFacets) {
            const dimensionId = `dim-${dimensionIndex}`;
            html += `
                <div class="card">
                    <div class="card-header" id="header-${dimensionId}">
                        <h2 class="mb-0">
                            <button class="btn btn-link btn-block text-left" type="button" data-bs-toggle="collapse" data-bs-target="#collapse-${dimensionId}" aria-expanded="true" aria-controls="collapse-${dimensionId}">
                                ${dimension}
                            </button>
                        </h2>
                    </div>
                    <div id="collapse-${dimensionId}" class="collapse ${dimensionIndex === 0 ? 'show' : ''}" aria-labelledby="header-${dimensionId}" data-bs-parent="#${accordionId}">
                        <div class="card-body">`;
            for (const subDimension in groupedFacets[dimension]) {
                html += `<h5>${subDimension}</h5>`;
                groupedFacets[dimension][subDimension].forEach(facet => {
                    html += `
                        <div class="form-check">
                            <input class="form-check-input facet-checkbox" type="checkbox" value="${facet.promptCharacteristic}" id="facet-${facet._id.$oid}">
                            <label class="form-check-label" for="facet-${facet._id.$oid}">
                                ${facet.promptCharacteristic}
                            </label>
                        </div>`;
                });
                html += '<hr>';
            }
            html += `   </div>
                    </div>
                </div>`;
            dimensionIndex++;
        }
        html += '</div>';
        facetsContainer.innerHTML = html;
    }

    // --- THE SCAFFOLD (ADR-0076 decision 3) ---

    function chosenFacets() {
        // Checked boxes in document order, i.e. the order of the accordion.
        const byId = new Map(loadedFacets.map(facet => [`facet-${facet._id.$oid}`, facet]));
        const checked = facetsContainer.querySelectorAll('.facet-checkbox:checked');
        return Array.from(checked).map(box => byId.get(box.id)).filter(Boolean);
    }

    function facetLine(facet) {
        // Verbatim: moderation finds personas by substring match on this text.
        return `- ${facet.promptCharacteristic}`;
    }

    function sortFacets(facets) {
        const bySection = {};
        const unmapped = new Map();
        facets.forEach(facet => {
            if (Object.prototype.hasOwnProperty.call(pageData.facetSections, facet.dimension)) {
                const section = pageData.facetSections[facet.dimension];
                (bySection[section] = bySection[section] || []).push(facet);
            } else {
                if (!unmapped.has(facet.dimension)) unmapped.set(facet.dimension, []);
                unmapped.get(facet.dimension).push(facet);
            }
        });
        return { bySection, unmapped };
    }

    function fallbackSection(unmapped) {
        if (unmapped.size === 0) return '';
        const lines = [`## ${pageData.fallbackSection}`];
        unmapped.forEach((facets, dimension) => {
            lines.push(`${dimension}:`, ...facets.map(facetLine));
        });
        return lines.join('\n');
    }

    function scaffold(facets) {
        // scaffold([]) equals persona_rules.scaffold_text().
        const { bySection, unmapped } = sortFacets(facets);
        const parts = pageData.sections.map(([heading, lines]) =>
            [`## ${heading}`, lines, ...(bySection[heading] || []).map(facetLine)].join('\n'));
        const fallback = fallbackSection(unmapped);
        if (fallback) parts.push(fallback);
        return parts.join('\n\n');
    }

    // --- THE EDITOR ---

    function mayReplaceText() {
        if (!textChanged || systemPromptTextarea.value.trim() === '') return true;
        return confirm('Den bisherigen Text ersetzen?');
    }

    function showEditor(text) {
        systemPromptTextarea.value = text;
        textChanged = false;
        resultsContainer.style.display = 'block';
        cancelEditButton.style.display = editingPromptId ? '' : 'none';
    }

    function startNew(text) {
        if (!mayReplaceText()) return;
        if (editingPromptId) {
            promptNameInput.value = '';
            promptDescriptionInput.value = '';
        }
        editingPromptId = null;
        showEditor(text);
    }

    function generatePromptText() {
        const facets = chosenFacets();
        if (facets.length === 0) {
            alert('Bitte wählen Sie mindestens eine Eigenschaft aus.');
            return;
        }
        startNew(scaffold(facets));
    }

    function startBlank() {
        startNew(scaffold([]));
    }

    function startEdit(li) {
        if (!mayReplaceText()) return;
        promptNameInput.value = li.dataset.name;
        promptDescriptionInput.value = li.dataset.description;
        editingPromptId = li.dataset.id;
        showEditor(li.dataset.promptText);
        resultsContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    function resetEditor() {
        promptNameInput.value = '';
        promptDescriptionInput.value = '';
        systemPromptTextarea.value = '';
        facetsContainer.querySelectorAll('.facet-checkbox:checked').forEach(cb => cb.checked = false);
        resultsContainer.style.display = 'none';
        cancelEditButton.style.display = 'none';
        editingPromptId = null;
        textChanged = false;
    }

    function openPlaceholder() {
        // The message of the first open placeholder, or null (ADR-0076 decision 4).
        const match = placeholderPattern.exec(systemPromptTextarea.value);
        if (!match) return null;
        return pageData.placeholderMessage.replace('{}', () => match[0]);
    }

    function stoppedAtPlaceholder() {
        const message = openPlaceholder();
        if (message) alert(message);
        return Boolean(message);
    }

    function saveErrorMessage(error) {
        const response = error.response;
        if (response && response.status === 400 && response.data && response.data.error) {
            return response.data.error;
        }
        return genericSaveError;
    }

    async function loadAndRenderSavedPrompts() {
        try {
            const response = await axios.get('/api/prompts');
            const prompts = response.data.filter(p => !p.is_standard);
            savedPromptsList.innerHTML = '';
            if (prompts.length === 0) {
                savedPromptsList.innerHTML = '<li class="list-group-item">Sie haben noch keine Personas gespeichert.</li>';
                return;
            }
            prompts.forEach(prompt => {
                const li = document.createElement('li');
                li.className = 'list-group-item d-flex justify-content-between align-items-center';
                li.dataset.id = prompt._id;
                li.dataset.name = prompt.name;
                li.dataset.description = prompt.description || '';
                li.dataset.promptText = prompt.prompt_text;
                li.innerHTML = renderPromptView(prompt);
                savedPromptsList.appendChild(li);
            });
        } catch (error) {
            console.error('Error loading saved prompts:', error);
            savedPromptsList.innerHTML = '<li class="list-group-item text-danger">Fehler beim Laden der gespeicherten Personas.</li>';
        }
    }

    function renderPromptView(prompt) {
        // A persona an operator has stopped stays in its owner's list, marked
        // and not editable, rather than disappearing (ADR-0062 FR-34). Ziffer 4
        // permits removal without *prior* notice; it does not require
        // concealment afterwards, and an object that vanishes silently invites
        // its owner to rebuild the same thing. Deleting it stays possible —
        // that is the owner's own decision about their own object.
        if (prompt.moderation_blocked) {
            return `
                <div>
                    <h6 class="my-0">${prompt.name} <span class="badge bg-secondary ms-1">stillgelegt</span></h6>
                    <small class="text-muted">Diese Persona wurde nach einer Prüfung stillgelegt und kann nicht mehr verwendet werden. Bei Fragen: info@shepwise.de</small>
                </div>
                <div>
                    <button class="btn btn-sm btn-outline-danger delete-prompt-btn">Löschen</button>
                </div>
            `;
        }
        return `
            <div>
                <h6 class="my-0">${prompt.name}</h6>
                <small class="text-muted">${prompt.description || ''}</small>
            </div>
            <div>
                <button class="btn btn-sm btn-outline-secondary edit-prompt-btn">Bearbeiten</button>
                <button class="btn btn-sm btn-outline-danger delete-prompt-btn">Löschen</button>
            </div>
        `;
    }

    async function handlePromptListClick(event) {
        const target = event.target;
        const li = target.closest('li.list-group-item');
        if (!li) return;

        const promptId = li.dataset.id;

        if (target.classList.contains('delete-prompt-btn')) {
            if (!confirm('Sind Sie sicher, dass Sie diese Persona löschen möchten?')) return;
            try {
                await axios.delete(`/api/prompts/${promptId}`);
                if (promptId === editingPromptId) resetEditor();
                loadAndRenderSavedPrompts();
            } catch (error) {
                console.error('Error deleting prompt:', error);
                alert('Fehler beim Löschen der Persona.');
            }
        } else if (target.classList.contains('edit-prompt-btn')) {
            // The text opens in the editor, not under the modal: the user
            // changes it first and saves to this persona (DEV_SPEC FR-20).
            startEdit(li);
        }
    }

    function sendPersona(payload) {
        // An edited persona is saved to itself; anything else is a new one.
        if (editingPromptId) return axios.put(`/api/prompts/${editingPromptId}`, payload);
        return axios.post('/api/prompts', payload);
    }

    async function handleSavePromptForm(event) {
        event.preventDefault();
        const name = promptNameInput.value.trim();
        const description = promptDescriptionInput.value.trim();
        const promptText = systemPromptTextarea.value;

        if (!name || !promptText) {
            alert('Bitte geben Sie einen Namen für die Persona an.');
            return;
        }
        if (stoppedAtPlaceholder()) return;

        try {
            await sendPersona({ name: name, description: description, prompt_text: promptText });
            savePromptModal.hide();
            resetEditor();
            loadAndRenderSavedPrompts();
        } catch (error) {
            console.error('Error saving prompt:', error);
            alert(saveErrorMessage(error));
        }
    }
    
    // --- EVENT LISTENERS ---
    startButton.addEventListener('click', function () {
        mainContent.style.display = 'block';
        startButton.style.display = 'none';
        loadFacetsAndRender();
    });
    
    startBlankButton.addEventListener('click', startBlank);
    cancelEditButton.addEventListener('click', resetEditor);
    systemPromptTextarea.addEventListener('input', () => { textChanged = true; });

    if (generatePromptButton) {
        generatePromptButton.addEventListener('click', generatePromptText);
    }
    if (savePromptButton) {
        savePromptButton.addEventListener('click', () => {
            // Checked before the modal asks for a name, so the user is stopped
            // while the open line is still in view. The editing id is kept:
            // saving an edited persona goes to that persona.
            if (stoppedAtPlaceholder()) return;
            savePromptModal.show();
        });
    }
    if (savePromptForm) {
        savePromptForm.addEventListener('submit', handleSavePromptForm);
    }
    if (savedPromptsList) {
        savedPromptsList.addEventListener('click', handlePromptListClick);
    }

    // --- INITIALIZATION ---
    if(savedPromptsList) {
        loadAndRenderSavedPrompts();
    }
};